import { useEffect, useState } from "react";
import { api, formatError } from "@/lib/api";
import { Card } from "./Dashboard";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plus, Power, X, Layers, Coffee, Clock } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { canEditShiftTemplates } from "@/lib/teamAdminPerms";

/**
 * Shift Templates - reusable cell payloads (start/end/break/entries/notes +
 * optional colour/icon). Different from the existing whole-week "Roster
 * Templates"; cell-level Shift Templates give admins a quick way to drop a
 * canonical day pattern (e.g. "Helpdesk Agent", "Weekend Cover") onto any
 * employee/day cell from the day editor or the bulk apply dialog.
 */
export default function ShiftTemplatesPage() {
  const { user } = useAuth();
  const [billingMe, setBillingMe] = useState(null);
  const [rows, setRows] = useState([]);
  const [workCats, setWorkCats] = useState([]);
  const [combos, setCombos] = useState([]);
  const [dialog, setDialog] = useState({ open: false, edit: null });
  // TP-1 -- centralised gate. Defaults to TRUE for company_admin /
  // super_admin so existing UI is unchanged. team_admin only gets
  // write access when TP-2 ships AND the company flag is on.
  const canEdit = canEditShiftTemplates(user, billingMe);

  const load = async () => {
    const [{ data }, { data: cats }, { data: cmb }] = await Promise.all([
      api.get("/shift-templates"),
      api.get("/categories"),
      api.get("/combos").catch(() => ({ data: [] })),
    ]);
    setRows(data);
    // TP-AT.RS.8.1 (Feb 2026) -- widen the assignment picker to
    // include Away categories (Break, Meeting, Training, ...) so
    // managers can build Work / Break / Work templates directly.
    // Leave categories are excluded because the backend refuses
    // them (`Templates cannot embed leave categories.`).
    setWorkCats(cats.filter(c =>
      (c.kind === "work" || c.kind === "away") && c.is_active));
    setCombos((cmb || []).filter(c => c.is_active !== false));
  };
  useEffect(() => {
    load();
    api.get("/billing/me").then(r => setBillingMe(r.data))
      .catch(() => setBillingMe({}));
  }, []);

  const toggleActive = async (tpl) => {
    try { await api.patch(`/shift-templates/${tpl.shift_template_id}`, { is_active: !tpl.is_active }); load(); }
    catch (e) { toast.error(formatError(e.response?.data?.detail)); }
  };

  return (
    <div>
      <div className="flex items-end justify-between mb-8">
        <div>
          <div className="eyebrow">Configuration</div>
          <h1 className="font-display text-4xl mt-1">Shift templates</h1>
          <p className="text-sm text-[#4B5773] mt-2 max-w-2xl">
            Reusable day patterns - start / end / break + optional assignments and
            notes. Drop them on any roster cell or bulk-apply across employees
            and days. Different from whole-week roster templates.
          </p>
        </div>
        {canEdit && (
          <Button onClick={()=>setDialog({ open: true, edit: null })} data-testid="add-shift-tpl-btn"
            className="rounded-lg bg-[#2563EB] hover:bg-[#1D4ED8]">
            <Plus className="h-4 w-4 mr-1"/>New template
          </Button>
        )}
      </div>

      {/* TP-AT.RS.8 -- Team Admins without shift-templates edit
          capability see the page (nav entry is theirs) but cannot
          create or edit. A brief inline note removes the "why is
          the New template button missing?" confusion. Workspace
          Admins always have canEdit=true so this note does not
          render for them. */}
      {!canEdit && (
        <div className="mb-6 rounded-lg border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-[#4B5773]"
             data-testid="shift-tpl-readonly-note">
          You can browse templates here and apply them from the roster editor.
          Ask your workspace admin if you need a new template or an edit.
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {rows.map(t => (
          <Card key={t.shift_template_id} className={t.is_active ? "" : "opacity-60"}>
            <div className="flex items-start justify-between mb-2 gap-3">
              <button
                onClick={()=>canEdit && setDialog({ open: true, edit: t })}
                disabled={!canEdit}
                className={`flex items-start gap-3 text-left flex-1 ${canEdit ? "" : "cursor-default"}`}
                data-testid={`shift-tpl-row-${t.shift_template_id}`}>
                <div className="h-8 w-1.5 rounded-full" style={{ background: t.color || "#2563EB" }} />
                <div>
                  <div className="font-display text-lg leading-tight">{t.name}</div>
                  <div className="text-xs text-[#4B5773] mt-0.5 flex items-center gap-2 flex-wrap">
                    <span className="inline-flex items-center gap-1"><Clock className="h-3 w-3"/>{t.start_time}-{t.end_time}</span>
                    {t.break_minutes > 0 && (
                      <span className="inline-flex items-center gap-1"><Coffee className="h-3 w-3"/>{t.break_minutes}m{t.break_start_time ? ` @ ${t.break_start_time}` : ""}</span>
                    )}
                  </div>
                </div>
              </button>
              {canEdit && (
                <Button size="sm" variant="ghost" onClick={()=>toggleActive(t)} data-testid={`shift-tpl-toggle-${t.shift_template_id}`}>
                  <Power className={`h-4 w-4 ${t.is_active ? "text-[#2563EB]" : "text-slate-400"}`}/>
                </Button>
              )}
            </div>
            {(t.entries || []).length > 0 && (
              <ul className="text-xs text-[#4B5773] space-y-0.5 ml-4">
                {t.entries.map((e, i) => {
                  const c = workCats.find(x => x.category_id === e.category_id);
                  const cmb = combos.find(x => x.combo_id === e.combo_id);
                  // TP-AT.RS.8.5 (Feb 2026) -- blank Work rows in
                  // templates render as "Work (blank)" so the
                  // manager can see the shape at a glance.
                  const label = cmb?.name || c?.name || "Work (blank)";
                  const times = (e.start_time && e.end_time)
                    ? `${e.start_time}-${e.end_time}`
                    : `${e.minutes} min`;
                  return (
                    <li key={i} data-testid={`shift-tpl-card-entry-${i}`}>
                      • {label} · {times}
                      {cmb ? <span className="ml-1 text-[9px] uppercase tracking-widest px-1 py-0.5 rounded-full bg-[#DBE7FE] text-[#1D4ED8]">combo</span> : null}
                    </li>
                  );
                })}
              </ul>
            )}
            {t.notes && <p className="mt-2 text-xs text-[#4B5773] italic">Note: {t.notes}</p>}
          </Card>
        ))}
        {!rows.length && (
          <Card className="md:col-span-2 lg:col-span-3">
            <div className="text-center py-6 text-sm text-[#4B5773]">
              No shift templates yet. Create one to quickly apply a day pattern.
            </div>
          </Card>
        )}
      </div>

      <ShiftTemplateDialog open={dialog.open} editing={dialog.edit}
        workCats={workCats} combos={combos}
        onClose={()=>setDialog({ open: false, edit: null })}
        onSaved={load}/>
    </div>
  );
}

function ShiftTemplateDialog({ open, editing, workCats, combos, onClose, onSaved }) {
  // TP-AT.RS.8.5 (Feb 2026) -- modal now uses the SAME row model
  // as the Roster cell editor + Roster Bulk Apply Shift + Rota
  // Style cell editor + Rota Style Bulk Apply Pattern. The old
  // top-level `Break (m)` + `Break Start` inputs were removed --
  // managers add breaks as "Break / away" rows and the backend
  // auto-mirrors `break_minutes` + `break_start_time` from Away
  // rows for legacy read-path compatibility. Editable minutes are
  // hidden from the normal view; duration is computed from
  // per-row Start / End clock inputs and shown as a read-only
  // chip. Advanced minutes toggle is retained for the rare case
  // where a template needs to preserve a legacy minutes-only row.
  const [form, setForm] = useState({
    name: "", start_time: "09:00", end_time: "17:00",
    notes: "", color: "#2563EB", icon: "",
  });
  const [entries, setEntries] = useState([]);
  const [showAdvancedMinutes, setShowAdvancedMinutes] = useState(false);

  useEffect(() => {
    if (open) {
      if (editing) {
        setForm({
          name: editing.name,
          start_time: editing.start_time, end_time: editing.end_time,
          notes: editing.notes || "",
          color: editing.color || "#2563EB",
          icon: editing.icon || "",
        });
        // Preserve every field on the loaded entries so an edit
        // round-trip doesn't drop `start_time` / `end_time`.
        setEntries((editing.entries || []).map(e => ({ ...e })));
      } else {
        setForm({ name: "", start_time: "09:00", end_time: "17:00",
                  notes: "", color: "#2563EB", icon: "" });
        setEntries([]);
      }
      setShowAdvancedMinutes(false);
    }
  }, [open, editing]);

  const _minsBetween = (s, e) => {
    if (!s || !e) return 0;
    const [sh, sm] = s.split(":").map(Number);
    const [eh, em] = e.split(":").map(Number);
    let m = (eh * 60 + em) - (sh * 60 + sm);
    if (m < 0) m += 24 * 60;
    return Math.max(0, m);
  };
  const updateEntry = (i, patch) => {
    setEntries(prev => prev.map((r, idx) => {
      if (idx !== i) return r;
      const next = { ...r, ...patch };
      if ("start_time" in patch || "end_time" in patch) {
        next.minutes = _minsBetween(next.start_time, next.end_time);
      }
      return next;
    }));
  };
  const removeEntry = (i) => {
    setEntries(prev => prev.filter((_, idx) => idx !== i));
  };
  const addWorkEntry = () => {
    // Blank Work row -- manager picks the activity later on apply
    // (or fills it here). Start/end default to the shift envelope.
    setEntries(prev => [...prev, {
      category_id: "", combo_id: null, _wanted_kind: "work",
      start_time: form.start_time || "09:00",
      end_time: form.end_time || "17:00",
      minutes: _minsBetween(form.start_time, form.end_time),
    }]);
  };
  const addBreakEntry = () => {
    // Break/Away row -- default to the tenant's Break Away
    // category so the manager doesn't have to reselect it.
    const brk = workCats.find(
      c => c.kind === "away" && (c.code === "break" || (c.name || "").toLowerCase() === "break"));
    setEntries(prev => [...prev, {
      category_id: brk ? brk.category_id : "",
      combo_id: null,
      _wanted_kind: "away",
      start_time: form.start_time || "13:00",
      end_time: form.start_time || "14:00",
      minutes: 0,
    }]);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) { toast.error("Name is required"); return; }
    if (form.start_time === form.end_time) {
      toast.error("End time must differ from start time"); return;
    }
    // Row validation:
    //  - Every row must have either category (work / away) OR be
    //    an explicitly blank Work row with valid clock window.
    //  - Every row must have a start_time + end_time (or minutes
    //    when the advanced-minutes toggle is on).
    for (const en of entries) {
      const hasCat = !!(en.category_id || en.combo_id);
      const hasWindow = !!(en.start_time && en.end_time);
      const hasMinutes = Number(en.minutes) > 0;
      if (!hasCat && !hasWindow) {
        toast.error("Each row needs an activity or a start / end time.");
        return;
      }
      if (!hasWindow && !hasMinutes) {
        toast.error("Each row needs a start / end time (or minutes in advanced view).");
        return;
      }
    }
    try {
      const payload = {
        name: form.name,
        start_time: form.start_time,
        end_time: form.end_time,
        notes: form.notes || null,
        entries: entries.map(en => ({
          category_id: en.category_id || null,
          combo_id: en.combo_id || null,
          minutes: Number(en.minutes || _minsBetween(en.start_time, en.end_time) || 0),
          start_time: en.start_time || null,
          end_time: en.end_time || null,
          note: en.note || null,
        })),
        color: form.color || null,
        icon: form.icon || null,
      };
      if (editing) await api.patch(`/shift-templates/${editing.shift_template_id}`, payload);
      else await api.post("/shift-templates", payload);
      toast.success(editing ? "Template updated" : "Template created");
      onSaved(); onClose();
    } catch (err) { toast.error(formatError(err.response?.data?.detail)); }
  };

  const del = async () => {
    if (!editing) return;
    if (!window.confirm("Delete this template? This cannot be undone.")) return;
    try { await api.delete(`/shift-templates/${editing.shift_template_id}`); toast.success("Template deleted"); onSaved(); onClose(); }
    catch (err) { toast.error(formatError(err.response?.data?.detail)); }
  };

  // Split categories by kind for the per-row picker (Work rows
  // only offer Work / combos; Break rows only offer Away).
  const workOptions = workCats.filter(c => c.kind === "work");
  const awayOptions = workCats.filter(c => c.kind === "away");

  return (
    <Dialog open={open} onOpenChange={(o)=>!o && onClose()}>
      <DialogContent fullscreenOnMobile className="max-w-2xl">
        <DialogHeader>
          <DialogTitle data-testid="shift-tpl-dialog-title">
            {editing ? "Edit shift template" : "New shift template"}
          </DialogTitle>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div className="grid grid-cols-3 gap-3">
            <div className="col-span-2">
              <Label className="eyebrow">Name</Label>
              <Input required value={form.name} data-testid="shift-tpl-name-input"
                onChange={(e)=>setForm({...form, name: e.target.value})}
                placeholder="e.g. Standard day" className="mt-2 h-11 rounded-lg"/>
            </div>
            <div>
              <Label className="eyebrow">Strip colour</Label>
              <Input type="color" value={form.color} data-testid="shift-tpl-color-input"
                onChange={(e)=>setForm({...form, color: e.target.value})}
                className="mt-2 h-11 rounded-lg"/>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="eyebrow">Start</Label>
              <Input type="time" step="1800" value={form.start_time} data-testid="shift-tpl-start"
                onChange={(e)=>setForm({...form, start_time: e.target.value})} className="mt-2 h-11 rounded-lg"/>
            </div>
            <div>
              <Label className="eyebrow">End</Label>
              <Input type="time" step="1800" value={form.end_time} data-testid="shift-tpl-end"
                onChange={(e)=>setForm({...form, end_time: e.target.value})} className="mt-2 h-11 rounded-lg"/>
            </div>
          </div>

          <div className="rounded-lg bg-[#F6F9FE] border border-slate-200 p-3">
            <div className="flex items-center justify-between mb-2 gap-2 flex-wrap">
              <div className="eyebrow">Assignments</div>
              <div className="flex items-center gap-2 flex-wrap">
                <Button type="button" variant="outline" size="sm"
                  data-testid="shift-tpl-add-work"
                  onClick={addWorkEntry}>
                  <Plus className="h-4 w-4 mr-1"/>Add work
                </Button>
                <Button type="button" variant="outline" size="sm"
                  data-testid="shift-tpl-add-away"
                  onClick={addBreakEntry}>
                  <Plus className="h-4 w-4 mr-1"/>Add break / away
                </Button>
              </div>
            </div>
            <div className="space-y-2" data-testid="shift-tpl-rows">
              {entries.length === 0 && (
                <div className="text-[11px] text-[#4B5773] px-2.5 py-3 border border-dashed border-slate-200 rounded-md text-center">
                  No rows yet. Use &quot;Add work&quot; or &quot;Add break / away&quot; to build the template shape.
                </div>
              )}
              {entries.map((en, i) => {
                const wantedKind = en._wanted_kind
                  || (en.category_id
                        ? (workCats.find(c => c.category_id === en.category_id)?.kind || "work")
                        : "work");
                const options = wantedKind === "away" ? awayOptions : workOptions;
                const pickerValue = en.combo_id ? `combo:${en.combo_id}` : (en.category_id ? `cat:${en.category_id}` : "");
                const onPickerChange = (v) => {
                  if (v.startsWith("combo:")) updateEntry(i, { combo_id: v.slice(6), category_id: "" });
                  else if (v.startsWith("cat:")) updateEntry(i, { category_id: v.slice(4), combo_id: null });
                  else updateEntry(i, { category_id: "", combo_id: null });
                };
                const mins = Number(en.minutes) > 0
                  ? Number(en.minutes)
                  : _minsBetween(en.start_time, en.end_time);
                return (
                  <div key={i} className="bg-white border border-slate-200 rounded-md p-2"
                       data-testid={`shift-tpl-row-${i}`}>
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-[10px] text-[#94A3B8] w-4 shrink-0">{i + 1}.</span>
                      <Select value={pickerValue} onValueChange={onPickerChange}>
                        <SelectTrigger
                          data-testid={`shift-tpl-row-cat-${i}`}
                          className={`flex-1 h-9 rounded-md min-w-[160px] ${
                            pickerValue
                              ? "border-slate-200"
                              : "border-amber-300 bg-amber-50 text-amber-900 font-medium"
                          }`}>
                          <SelectValue placeholder={
                            wantedKind === "away"
                              ? "Select break / away..."
                              : "Select work activity..."} />
                        </SelectTrigger>
                        <SelectContent>
                          {options.map(c => (
                            <SelectItem key={c.category_id} value={`cat:${c.category_id}`}>
                              {c.kind === "away" ? (
                                <span className="inline-flex items-center gap-2">
                                  {c.name}
                                  <span className="text-[9px] uppercase tracking-widest px-1.5 py-0.5 rounded-full bg-[#EDE9FE] text-[#5B21B6]">Away</span>
                                </span>
                              ) : c.name}
                            </SelectItem>
                          ))}
                          {wantedKind === "work" && combos.length > 0 && (
                            <>
                              <div className="px-2 py-1 text-[10px] uppercase tracking-widest text-[#4B5773]">Combos</div>
                              {combos.map(c => (
                                <SelectItem key={c.combo_id} value={`combo:${c.combo_id}`}>
                                  <span className="inline-flex items-center gap-2">
                                    {c.name}
                                    <span className="text-[9px] uppercase tracking-widest px-1.5 py-0.5 rounded-full bg-[#DBE7FE] text-[#1D4ED8]">Combo</span>
                                  </span>
                                </SelectItem>
                              ))}
                            </>
                          )}
                        </SelectContent>
                      </Select>
                      <Input
                        type="time"
                        step="900"
                        value={en.start_time || ""}
                        onChange={(e)=>updateEntry(i, { start_time: e.target.value })}
                        data-testid={`shift-tpl-row-start-${i}`}
                        aria-label="Start time"
                        className="h-9 w-[104px] text-sm"/>
                      <span className="text-[10px] text-[#94A3B8]">-</span>
                      <Input
                        type="time"
                        step="900"
                        value={en.end_time || ""}
                        onChange={(e)=>updateEntry(i, { end_time: e.target.value })}
                        data-testid={`shift-tpl-row-end-${i}`}
                        aria-label="End time"
                        className="h-9 w-[104px] text-sm"/>
                      <span
                        data-testid={`shift-tpl-row-duration-${i}`}
                        className={`text-[10px] px-2 py-1 rounded-full whitespace-nowrap ${
                          mins > 0 ? "bg-[#DBE7FE] text-[#1D4ED8]" : "bg-slate-100 text-slate-400"
                        }`}>
                        {mins > 0
                          ? `${Math.floor(mins / 60)}h${mins % 60 ? ` ${mins % 60}m` : ""}`
                          : "set time"}
                      </span>
                      {showAdvancedMinutes && (
                        <Input
                          type="number" step="15" min="0"
                          value={en.minutes || 0}
                          onChange={(e)=>updateEntry(i, { minutes: Number(e.target.value) })}
                          data-testid={`shift-tpl-row-minutes-${i}`}
                          aria-label="Minutes (advanced)"
                          className="h-9 w-20 text-sm"/>
                      )}
                      <Button type="button" variant="ghost" size="sm"
                        data-testid={`shift-tpl-row-remove-${i}`}
                        onClick={()=>removeEntry(i)}
                        className="text-[#94A3B8] hover:text-red-600">
                        <X className="h-4 w-4"/>
                      </Button>
                    </div>
                  </div>
                );
              })}
              {entries.length > 0 && (
                <div className="pt-1 text-right">
                  <button
                    type="button"
                    onClick={()=>setShowAdvancedMinutes(v => !v)}
                    data-testid="shift-tpl-advanced-minutes-toggle"
                    className="text-[10px] text-[#94A3B8] hover:text-[#0B1A36] underline-offset-2 hover:underline">
                    {showAdvancedMinutes ? "Hide minutes" : "Advanced: show minutes"}
                  </button>
                </div>
              )}
            </div>
          </div>

          <div>
            <Label className="eyebrow">Notes (optional)</Label>
            <Input value={form.notes} data-testid="shift-tpl-notes-input"
              onChange={(e)=>setForm({...form, notes: e.target.value})}
              placeholder="Default notes applied with this template" className="mt-2 h-11 rounded-lg"/>
          </div>

          <DialogFooter>
            {editing && (
              <Button type="button" variant="ghost" data-testid="shift-tpl-delete-btn" onClick={del}
                className="text-red-600 hover:text-red-700 hover:bg-red-50">Delete</Button>
            )}
            <Button type="button" variant="outline" data-testid="shift-tpl-cancel-btn" onClick={onClose}
              className="rounded-lg border-slate-200">Close</Button>
            <Button type="submit" data-testid="shift-tpl-submit-btn"
              className="rounded-lg bg-[#2563EB] hover:bg-[#1D4ED8]">
              {editing ? "Save" : "Create"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
